import React, { useCallback, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ExternalLink, Loader2, MessageCircle, Sheet } from "lucide-react";
import TemplatePreviewPanel from "@/components/TemplatePreviewPanel";
import { formatTemplateName, type TemplatePreviewData } from "@/lib/templatePreview";

export interface AssistantPlanUI {
  conversation_step?: string;
  sheet_headers_snapshot?: string[];
  sheet_catalog?: { sheet_id: string; name: string }[];
  template_catalog?: { template_id: string; name: string }[];
  column_mapping_notes?: string[];
  campaign_id?: string;
  campaign_type?: string | null;
  template_mode?: string | null;
  active_template_preview?: TemplatePreviewData | null;
  pending_template_variables?: string[];
  message_template?: string | null;
  whatsapp_template_name?: string | null;
  missing_connections?: string[];
  whatsapp_connected?: boolean | null;
  sheets_connected?: boolean | null;
  sheet_url?: string | null;
  sheet_mode?: string | null;
  sheet_name?: string | null;
  google_sheet_id?: string | null;
}

interface AssistantMessageBlocksProps {
  plan?: AssistantPlanUI | null;
  onConnectionsUpdated?: () => void;
  onGoToCampaigns?: () => void;
}

const UI_FIRST_STEPS = new Set([
  "connections",
  "sheet_pick",
  "final_confirm",
]);

export function shouldRenderBlocksBeforeText(step?: string): boolean {
  return Boolean(step && UI_FIRST_STEPS.has(step));
}

export function shouldRenderBlocksAfterText(step?: string, _hasPreview?: boolean): boolean {
  return (
    step === "done" ||
    step === "add_contacts" ||
    step === "template_confirm" ||
    step === "template_pick"
  );
}

export default function AssistantMessageBlocks({
  plan,
  onConnectionsUpdated,
  onGoToCampaigns,
}: AssistantMessageBlocksProps) {
  const [connecting, setConnecting] = useState<"whatsapp" | "sheets" | null>(null);

  const connectWhatsApp = useCallback(() => {
    setConnecting("whatsapp");
    window.open("/whatsapp", "_blank", "noopener,noreferrer");
    toast.message("Conecta WhatsApp en la ventana abierta", {
      description: "Cuando termines, vuelve aquí y pulsa «Ya conecté».",
    });
    setConnecting(null);
  }, []);

  const connectSheets = useCallback(async () => {
    setConnecting("sheets");
    try {
      const res = await api.get("/google-sheets/auth", {
        params: { context: "campaign-assistant" },
      });
      const url = res.data?.authorization_url || res.data?.auth_url || res.data;
      if (typeof url === "string" && url.startsWith("http")) {
        window.open(url, "_blank", "noopener,noreferrer");
        toast.message("Autoriza Google Sheets en la ventana abierta", {
          description: "Cuando termines, vuelve aquí y pulsa «Ya conecté».",
        });
      } else {
        toast.error("No se pudo obtener la URL de Google");
      }
    } catch {
      toast.error("Error al iniciar conexión con Google Sheets");
    } finally {
      setConnecting(null);
    }
  }, []);

  const openSheet = useCallback(() => {
    const url = plan?.sheet_url;
    if (url && url.startsWith("http")) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("No hay enlace a la hoja todavía");
    }
  }, [plan?.sheet_url]);

  if (!plan) return null;

  const step = plan.conversation_step;
  const headers = plan.sheet_headers_snapshot || [];
  const sheetCatalog = plan.sheet_catalog || [];
  const mappingNotes = plan.column_mapping_notes || [];
  const missing = plan.missing_connections || [];
  const isRenewal = plan.campaign_type === "renewal";

  const showConnections =
    step === "connections" ||
    (missing.length > 0 && (step === "welcome" || step === "connections"));
  const showSheetCatalog = sheetCatalog.length > 0 && step === "sheet_pick";
  const showHeaders = headers.length > 0 && step === "final_confirm";
  const showMapping =
    mappingNotes.length > 0 && step === "final_confirm";
  const showSheetGuide =
    step === "final_confirm" && Boolean(plan.sheet_url || plan.google_sheet_id || headers.length);
  const showMetaWarning =
    Boolean(plan.campaign_id) && (step === "done" || step === "add_contacts");

  // Existing approved template only (do not affect new drafts).
  const showExistingTemplatePreview =
    plan.template_mode === "existing" &&
    (step === "template_confirm" || step === "template_pick") &&
    Boolean(plan.active_template_preview);
  const existingVars =
    plan.pending_template_variables && plan.pending_template_variables.length > 0
      ? plan.pending_template_variables
      : [];
  const showExistingVariables =
    plan.template_mode === "existing" &&
    step === "template_confirm" &&
    existingVars.length > 0;

  if (
    !showConnections &&
    !showSheetCatalog &&
    !showHeaders &&
    !showMapping &&
    !showSheetGuide &&
    !showMetaWarning &&
    !showExistingTemplatePreview &&
    !showExistingVariables
  ) {
    return null;
  }

  const formattedExistingPreview = plan.active_template_preview
    ? {
        ...plan.active_template_preview,
        name:
          formatTemplateName(plan.active_template_preview.name) ||
          plan.active_template_preview.name ||
          plan.whatsapp_template_name ||
          undefined,
      }
    : null;

  return (
    <div className="mt-2 space-y-2 mr-6">
      {showConnections && missing.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/90 px-3 py-3 text-sm text-amber-950 space-y-2">
          <div className="font-medium">Conexiones necesarias</div>
          <div className="flex flex-wrap gap-2">
            {missing.includes("whatsapp") && (
              <Button
                size="sm"
                className="bg-[#25D366] hover:bg-[#1ebe57] text-white"
                onClick={connectWhatsApp}
                disabled={connecting === "whatsapp"}
              >
                {connecting === "whatsapp" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <MessageCircle className="h-4 w-4 mr-1" />
                )}
                Conectar WhatsApp
              </Button>
            )}
            {missing.includes("sheets") && (
              <Button
                size="sm"
                variant="outline"
                className="border-teal-300 text-teal-800"
                onClick={connectSheets}
                disabled={connecting === "sheets"}
              >
                {connecting === "sheets" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Sheet className="h-4 w-4 mr-1" />
                )}
                Conectar hojas
              </Button>
            )}
            {onConnectionsUpdated && (
              <Button size="sm" variant="secondary" onClick={onConnectionsUpdated}>
                Ya conecté
              </Button>
            )}
          </div>
        </div>
      )}

      {showSheetCatalog && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
          <div className="font-medium text-gray-800 mb-1">Hojas conectadas</div>
          <ul className="max-h-48 overflow-y-auto space-y-0.5 text-gray-700">
            {sheetCatalog.map((s) => (
              <li key={s.sheet_id}>{s.name}</li>
            ))}
          </ul>
        </div>
      )}

      {showHeaders && (
        <div className="rounded-lg border border-blue-100 bg-blue-50/80 px-3 py-2 text-xs text-blue-900">
          <div className="font-medium mb-1">Columnas de tu hoja</div>
          <div className="flex flex-wrap gap-1">
            {headers.map((h) => (
              <span
                key={h}
                className="inline-flex px-2 py-0.5 rounded-full bg-white border border-blue-200 text-blue-800"
              >
                {h}
              </span>
            ))}
          </div>
        </div>
      )}

      {showMapping && (
        <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
          <div className="font-medium mb-1">Variables de la plantilla</div>
          <ul className="space-y-0.5">
            {mappingNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {showSheetGuide && (
        <div className="rounded-xl border border-teal-200 bg-teal-50/90 px-3 py-3 text-sm text-teal-950 space-y-2">
          <div className="font-medium">Tu hoja de datos</div>
          <p className="text-xs text-teal-900/90 leading-relaxed">
            Ábrela para ver las columnas y variables
            {plan.sheet_name ? ` en «${plan.sheet_name}»` : ""}. Así llenarás cada casilla:
          </p>
          <ul className="text-xs text-teal-900/90 space-y-1 list-disc pl-4">
            <li>
              <span className="font-medium">Teléfono:</span> sin +57 si es de Colombia
              (ej. 3001234567). Si es internacional, incluye el código (ej. +1 305 555 1234).
            </li>
            {isRenewal ? (
              <li>
                <span className="font-medium">Fecha del recordatorio:</span> día/mes/año
                (ej. 05/09/2026).
              </li>
            ) : (
              <>
                <li>
                  <span className="font-medium">Fecha:</span> día/mes/año (ej. 05/09/2026).
                </li>
                <li>
                  <span className="font-medium">Hora:</span> 24 horas (15:30) o 12 horas (3:30 PM).
                </li>
              </>
            )}
            <li>
              El resto de columnas (nombre, mascota, etc.) se escriben en texto normal, igual que en el mensaje.
            </li>
          </ul>
          {plan.sheet_url ? (
            <Button
              size="sm"
              className="bg-teal-700 hover:bg-teal-800 text-white"
              onClick={openSheet}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Abrir la hoja
            </Button>
          ) : (
            <p className="text-xs text-teal-800/80">
              La hoja quedó vinculada; ábrela desde Google Sheets con el mismo nombre.
            </p>
          )}
        </div>
      )}

      {showExistingTemplatePreview && formattedExistingPreview && (
        <TemplatePreviewPanel
          embedded
          scrollable
          showSectionLabel={false}
          preview={formattedExistingPreview}
        />
      )}

      {showExistingVariables && (
        <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
          <div className="font-medium mb-1">Variables de esta plantilla</div>
          <ul className="space-y-0.5">
            {existingVars.map((v, i) => (
              <li key={`${v}-${i}`}>
                {v.startsWith("{{") ? v : `{{${i + 1}}}`} — se mapeará a una columna de la hoja
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-amber-800/80">
            Al crear o vincular la hoja, estas variables se mapean automáticamente a columnas
            (igual que con un mensaje nuevo).
          </p>
        </div>
      )}

      {showMetaWarning && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-950 space-y-2">
          <div className="font-medium">Plantilla pendiente de Meta</div>
          <p className="text-xs leading-relaxed text-amber-900/90">
            Tu campaña ya está creada. Se activa cuando Meta apruebe la plantilla;
            entonces tus mensajes se enviarán según los recordatorios configurados.
          </p>
          <p className="text-xs leading-relaxed text-amber-900/90">
            Ve a Campañas: ahí puedes ver los detalles de tu campaña creada.
          </p>
          {onGoToCampaigns && (
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={onGoToCampaigns}
            >
              Ir a Campañas
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
